import React, {useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {idToTitleMapType, objectType, oxygenUserType} from '../../constants/proptype-validation';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {ISSUE_ATTRIBUTES_DATA_TYPES} from '../../constants/issue-attributes';
import i18n from '../../utils/i18n';
import {
  ACTIVITY_VERBS,
  IssueCreateActivityFieldNameMapping,
  IssuesActivityFieldNameMapping,
  IssuesActivityIgnoredFields
} from '../../constants/activity';
import {DOCUMENT_ATTACHMENT_TYPE, FILE_ATTACHMENT_TYPE, PHOTO_ATTACHMENT_TYPE} from '../../constants/attachments';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {toUtcLongDate} from '../../utils/date-helpers';
import ellipsis from '../../utils/ellipsis';
import moment from 'moment';
import ActivityBlock, {ActivitySubComponents} from '@adsk/bim360-matrix-react-activity-block';
import ActivityCommentBlock from '@adsk/bim360-matrix-react-activity-comment-block';
import {DEFAULT_AVATAR} from '@adsk/bim360-matrix-react-helpers/es/consts';
import InfiniteScroll from '@adsk/bim360-matrix-react-infinite-scroll';
import Loader from '@adsk/bim360-matrix-react-loader';
import ThumbnailPicture from '@adsk/bim360-matrix-react-thumbnail-picture';
import {viewTypes} from '../../constants/view-types';
import cx from 'classnames';
import ActivityChangeStatusNode from './ActivityChangeStatusNode';
import {getActivityBlocksAttachments, getChangesFromActivity} from '../../utils/activityHelper';
import ActivityChangeLocationNodeContainer from './ActivityChangeLocationNodeContainer';
import {toV2} from '../../utils/version-converters';
import IssueAttachmentFileItem from './IssueAttachmentFileItem';
import isEmpty from 'lodash/isEmpty';
import {sanitizeAndLinkifyHtml} from '../../utils/html-helpers';

const getAttachmentsFieldName = (change, attachmentType) => {
  if (change.after) {
    const addAttachmentNameMapping = {
      [PHOTO_ATTACHMENT_TYPE]: 'addedPhoto',
      [DOCUMENT_ATTACHMENT_TYPE]: 'addedDoc',
      [FILE_ATTACHMENT_TYPE]: 'addedFile'
    };
    const addAttachmentName = addAttachmentNameMapping[attachmentType];

    return change.after.length > 1
      ? i18n.t(`activity.nameMapping.${addAttachmentName}s`, {number: change.after.length})
      : i18n.t(`activity.nameMapping.${addAttachmentName}`);
  } else {
    return change.before.length > 1
      ? i18n.t('activity.nameMapping.removedAttachments', {number: change.before.length})
      : i18n.t('activity.nameMapping.removedAttachment');
  }
};

const getFieldNameByProperty = (property, isCreateIssueBlock) => {
  if (isCreateIssueBlock && IssueCreateActivityFieldNameMapping[property]) {
    return i18n.t(IssueCreateActivityFieldNameMapping[property]);
  } else if (IssuesActivityFieldNameMapping[property]) {
    return i18n.t(IssuesActivityFieldNameMapping[property]);
  } else {
    if (IssuesActivityIgnoredFields.includes(property)) {
      return '';
    }

    return property;
  }
};

const getCustomAttributeActivity = (change, issueCustomAttributes) => {
  const item = {fieldName: change.title};
  switch (change.type) {
    case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST: {
      const issueAttribute = issueCustomAttributes[change.id];
      const selectedOption = issueAttribute
        ? issueAttribute.metadata.list.options.find(option => option.id === change.value)
        : null;
      item.fieldValue = selectedOption ? selectedOption.value : i18n.t('activity.formerSingleSelectValue');
      break;
    }
    case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT:
    case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH:
      item.component = ActivitySubComponents.LONG_TEXT;
      item.fieldValue = sanitizeAndLinkifyHtml(change.value);
      break;
    default:
      item.fieldValue = change.value;
  }

  return item;
};

const getRootCauseText = (rootCauseEntitiesMap, rootCauseId) => {
  const rootCause = rootCauseEntitiesMap[rootCauseId];
  return rootCause ? rootCause.title : i18n.t('activity.unknownRootCause');
};

const getAttachmentsPhotosNode = (change, thumbnailsByUrn, showAttachmentsInGallery) => {
  if (!change.after.every(picture => thumbnailsByUrn[picture.attributes.urn])) {
    return {
      isLoading: true,
      component: (
        <div className="IssuePreviewActivityContent__thumbnail-loader-wrapper">
          <Loader />
        </div>
      )
    };
  }

  let thumbnailPictures;
  if (change.after) {
    thumbnailPictures = change.after.map(picture => {
      const thumbnailsForAChange = thumbnailsByUrn[picture.attributes.urn];
      const url = thumbnailsForAChange.attachmentUrl;
      const name = ellipsis(picture.attributes.name, 3, 120, 13);
      const pictureProps = {};
      if (!picture.attributes.hasURL) {
        pictureProps.name = name;
      } else if (!url) {
        pictureProps.error = true;
      } else {
        pictureProps.src = url;
        pictureProps.name = name;
        pictureProps.onClick = () => showAttachmentsInGallery([{name, urn: picture.attributes.urn}], 0);
      }
      return (
        <ThumbnailPicture
          key={picture.attributes.urn}
          className="IssuePreviewActivityContent__thumbnail-picture"
          picture={pictureProps}
        />
      );
    });
  } else {
    thumbnailPictures = change.before.map((picture, pictureIndex) => (
      <ThumbnailPicture key={pictureIndex} className="IssuePreviewActivityContent__thumbnail-picture" picture={{}} />
    ));
  }

  return {isLoading: false, component: thumbnailPictures};
};

const getAttachmentsPhotosActivity = (change, thumbnailsByUrn, showAttachmentsInGallery) => {
  const item = {};
  item.fieldName = getAttachmentsFieldName(change, PHOTO_ATTACHMENT_TYPE);
  const attachmentNode = getAttachmentsPhotosNode(change, thumbnailsByUrn, showAttachmentsInGallery);
  item.fieldValue = attachmentNode.component;
  if (!attachmentNode.isLoading) {
    item.component = ActivitySubComponents.ATTACHMENTS_BLOCK;
  }

  return item;
};

const getAttachmentsDocsActivity = (change, container) => {
  const item = {};
  const attachments = change.after.map(attachment => attachment.attributes);
  item.fieldName = getAttachmentsFieldName(change, DOCUMENT_ATTACHMENT_TYPE);
  item.fieldValue = (
    <div className="IssuePreviewActivityContent__activity-attachment-list">
      {attachments.map(attachment => (
        <IssueAttachmentFileItem attachment={attachment} key={attachment.id} container={container} />
      ))}
    </div>
  );

  return item;
};

const getAttachmentsFilesActivity = (change, container) => {
  const item = {};
  const attachments = change.after.map(attachment => attachment.attributes);
  item.fieldName = getAttachmentsFieldName(change, FILE_ATTACHMENT_TYPE);
  item.fieldValue = (
    <div className="IssuePreviewActivityContent__activity-attachment-list">
      {attachments.map(attachment => (
        <IssueAttachmentFileItem attachment={attachment} key={attachment.id} container={container} />
      ))}
    </div>
  );
  item.hideBulletLine = true;

  return item;
};

const getActivityLines = (
  activityBlock,
  isCreateIssueBlock,
  {
    rootCauseEntitiesMap,
    issueTypesAndSubTypesTitleMap,
    thumbnailsByUrn,
    showAttachmentsInGallery,
    projectEntities,
    currentUser,
    issueCustomAttributes,
    getEntityDisplayNameById,
    documentCollection
  },
  container,
  contentElementRef
) => {
  const activities = activityBlock.activityList;
  const activityLines = [];
  const length = activities.length;
  for (let i = 0; i < length; i++) {
    const activityChanges = getChangesFromActivity(activities[i]);
    for (let j = 0; j < activityChanges.length; j++) {
      const currentChange = activityChanges[j];
      const property = toV2(currentChange.property);
      let currentItem = {
        fieldName: getFieldNameByProperty(property, isCreateIssueBlock)
      };

      switch (property) {
        case ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO:
        case ISSUE_ATTRIBUTE_NAMES.OWNER_ID:
          currentItem.fieldValue = getEntityDisplayNameById(
            currentChange.after,
            i18n.t('activity.formerUser'),
            i18n.t('user.unspecified')
          );
          break;
        case ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE:
          continue;
        case ISSUE_ATTRIBUTE_NAMES.TITLE:
          currentItem.fieldValue = sanitizeAndLinkifyHtml(currentChange.after);
          currentItem.component = ActivitySubComponents.LONG_TEXT;
          break;
        case ISSUE_ATTRIBUTE_NAMES.DESCRIPTION:
          // Edge case - when creating an issue we don't want to show the new empty string value if the user hasn't entered one
          if (!currentChange.before && !currentChange.after) {
            continue;
          } else if (currentChange.before && currentChange.before.length > 0 && !currentChange.after) {
            currentItem.fieldName = i18n.t('activity.nameMapping.descriptionDeleted');
            currentItem.fieldValue = '';
          } else {
            currentItem.fieldValue = sanitizeAndLinkifyHtml(currentChange.after);
            currentItem.component = ActivitySubComponents.LONG_TEXT;
          }
          break;
        case ISSUE_ATTRIBUTE_NAMES.LOCATION_ID: {
          currentItem.fieldValue = currentChange.after ? (
            <ActivityChangeLocationNodeContainer change={currentChange} container={contentElementRef} />
          ) : (
            i18n.t('issueAttributes.lbsLocation.unspecified')
          );
          break;
        }
        case ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS:
          if (currentChange.before && currentChange.before.length > 0 && !currentChange.after) {
            currentItem.fieldName = i18n.t('activity.nameMapping.locationDescriptionDeleted');
            currentItem.fieldValue = '';
          } else {
            currentItem.fieldValue = sanitizeAndLinkifyHtml(currentChange.after);
            currentItem.component = ActivitySubComponents.LONG_TEXT;
          }
          break;
        case ISSUE_ATTRIBUTE_NAMES.DUE_DATE:
          currentItem.fieldValue = currentChange.after
            ? toUtcLongDate(currentChange.after)
            : i18n.t('dueDate.unspecified');
          break;
        case ISSUE_ATTRIBUTE_NAMES.STATUS:
          currentItem.fieldValue = <ActivityChangeStatusNode change={currentChange} />;
          break;
        case ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID:
          currentItem.fieldValue = currentChange.after
            ? getRootCauseText(rootCauseEntitiesMap, currentChange.after)
            : i18n.t('rootCauses.unspecified');
          break;
        case ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID:
          currentItem.fieldValue = issueTypesAndSubTypesTitleMap[currentChange.after];
          break;
        case ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID:
        case ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID_ACTIVITY:
          currentItem.fieldValue = issueTypesAndSubTypesTitleMap[currentChange.after];
          break;
        case ISSUE_ATTRIBUTE_NAMES.ATTACHMENT: {
          if (currentChange.attachmentType === PHOTO_ATTACHMENT_TYPE) {
            currentItem = getAttachmentsPhotosActivity(currentChange, thumbnailsByUrn, showAttachmentsInGallery);
          } else if (currentChange.attachmentType === DOCUMENT_ATTACHMENT_TYPE) {
            currentItem = getAttachmentsDocsActivity(currentChange, container);
          } else {
            currentItem = getAttachmentsFilesActivity(currentChange, container);
          }
          break;
        }
        case ISSUE_ATTRIBUTE_NAMES.DELETED_AT:
          currentItem.fieldValue = '';
          break;
        case ISSUE_ATTRIBUTE_NAMES.COMMENT_BODY:
          currentItem.component = ActivityCommentBlock;
          currentItem.alignLeft = activities.length === 1 && activityChanges.length === 1;
          currentItem.props = {
            comment: currentChange.after,
            projectEntities,
            currentUser,
            activityTime: moment(new Date(activityBlock.firstActivityDate)),
            escapeHtml: true,
            className: 'IssuePreviewActivityContent__activity-comment-block'
          };
          break;
        case ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES:
          currentChange.after?.forEach(item =>
            activityLines.push(getCustomAttributeActivity(item, issueCustomAttributes))
          );
          continue;
        case ISSUE_ATTRIBUTE_NAMES.OFFICIAL_RESPONSE:
          if (currentChange.before && currentChange.before.length > 0 && !currentChange.after) {
            currentItem.fieldName = i18n.t('activity.nameMapping.responseDeleted');
            currentItem.fieldValue = '';
          } else {
            currentItem.fieldValue = sanitizeAndLinkifyHtml(currentChange.after);
            currentItem.component = ActivitySubComponents.LONG_TEXT;
          }
          break;
        case 'targetUrn':
          if (!currentChange.after) {
            currentItem.fieldName = i18n.t('activity.nameMapping.unlinkDoc');
            break;
          }
          const document = documentCollection[currentChange.after];
          if (isEmpty(document)) {
            currentItem.fieldName = i18n.t('activity.nameMapping.targetUrnFallback');
          } else {
            currentItem.fieldValue = document?.name ?? document?.currentVersion?.title;
          }
          break;
        default:
          currentItem.fieldValue = currentChange.after;
          break;
      }

      activityLines.push(currentItem);
    }
  }

  return activityLines;
};

const getUserDisplayValueById = (oxygen, actor, getEntityDisplayNameById) =>
  oxygen.users[actor.id] || {
    name: getEntityDisplayNameById(actor.id),
    profileImg50: DEFAULT_AVATAR
  };

const IssuePreviewActivityContent = ({
  issueId,
  activityBlocks,
  oxygen,
  projectEntities,
  rootCauseEntitiesMap,
  issueTypesAndSubTypesTitleMap,
  currentUser,
  hasPreviousActivities,
  loadMoreActivities,
  showAttachmentsInGallery,
  viewType,
  fieldIssuesActivityFetchAttachments,
  thumbnailsByUrn,
  issueCustomAttributes,
  dummyActivityBlock,
  getEntityDisplayNameById,
  container,
  documentCollection,
  contentElementRef
}) => {
  useEffect(() => {
    fieldIssuesActivityFetchAttachments(getActivityBlocksAttachments(activityBlocks));
  }, [activityBlocks, fieldIssuesActivityFetchAttachments]);

  const getActivityBlockItem = useCallback(activityBlock => {
    const user = getUserDisplayValueById(oxygen, activityBlock.actor, getEntityDisplayNameById);
    const isCreateIssueBlock = activityBlock.activityList[0].type === ACTIVITY_VERBS.CREATE;
    const activityLines = getActivityLines(
      activityBlock,
      isCreateIssueBlock,
      {
        oxygen,
        rootCauseEntitiesMap,
        issueTypesAndSubTypesTitleMap,
        thumbnailsByUrn,
        showAttachmentsInGallery,
        projectEntities,
        currentUser,
        issueCustomAttributes,
        getEntityDisplayNameById,
        documentCollection
      },
      container,
      contentElementRef
    );

    // This if condition is here in case all of the activity lines are ignored fields, and we prevent showing just the header
    if (activityLines.length === 0) {
      return null;
    }

    const items = !isCreateIssueBlock
      ? activityLines
      : [
        {fieldValue: i18n.t('activity.createIssue', {issueType: TYPE_QUALITY_ISSUES})},
        {
          fieldValue: (
            <div className="IssuePreviewActivityContent__nested-block">
              <ActivityBlock
                headerComponent={({className}) => (
                  <div className={`${className} IssuePreviewActivityContent__nested-block-empty-header`} />
                )}
                items={activityLines}
                container={container}
              />
            </div>
          )
        }
      ];

    return (
      <div
        key={new Date(activityBlock.firstActivityDate).getTime()}
        className="IssuePreviewActivityContent__block"
        data-testid="IssuePreviewActivityContent__block"
      >
        <ActivityBlock
          user={user}
          activityTime={moment(new Date(activityBlock.firstActivityDate))}
          items={items}
          container={container}
        />
      </div>
    );
  }, [
    currentUser,
    documentCollection,
    getEntityDisplayNameById,
    issueCustomAttributes,
    issueTypesAndSubTypesTitleMap,
    oxygen,
    projectEntities,
    rootCauseEntitiesMap,
    showAttachmentsInGallery,
    thumbnailsByUrn,
    container,
    contentElementRef
  ]);

  const className = cx('IssuePreviewActivityContent', {
    'IssuePreviewActivityContent--narrow': viewType === viewTypes.DOCUMENT
  });

  const items = activityBlocks.map(activityBlock => getActivityBlockItem(activityBlock));

  return (
    <div className={className}>
      <InfiniteScroll
        items={dummyActivityBlock ? items.concat(getActivityBlockItem(dummyActivityBlock)) : items}
        loadMore={() => loadMoreActivities({issueId})}
        hasMore={hasPreviousActivities}
        className="IssuePreviewActivityContent__scroll-background"
        direction={InfiniteScroll.Direction.UP}
      />
    </div>
  );
};

IssuePreviewActivityContent.displayName = 'IssuePreviewActivityContent';

IssuePreviewActivityContent.propTypes = {
  issueId: PropTypes.string.isRequired,
  activityBlocks: PropTypes.array.isRequired,
  oxygen: objectType.isRequired,
  projectEntities: PropTypes.array,
  rootCauseEntitiesMap: PropTypes.object.isRequired,
  issueTypesAndSubTypesTitleMap: idToTitleMapType.isRequired,
  currentUser: oxygenUserType,
  hasPreviousActivities: PropTypes.bool,
  loadMoreActivities: PropTypes.func,
  showAttachmentsInGallery: PropTypes.func,
  viewType: PropTypes.string,
  fieldIssuesActivityFetchAttachments: PropTypes.func.isRequired,
  thumbnailsByUrn: PropTypes.object,
  issueCustomAttributes: PropTypes.object,
  dummyActivityBlock: PropTypes.object,
  getEntityDisplayNameById: PropTypes.func.isRequired,
  container: PropTypes.object,
  documentCollection: PropTypes.object.isRequired,
  contentElementRef: PropTypes.object
};

export default IssuePreviewActivityContent;
